import { IonContent, IonPage, useIonToast } from '@ionic/react';
import { FC, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { lastValueFrom } from 'rxjs';

import { ReduxCommandCreator } from 'appRedux/actions';
import { IState, useAppSelector } from 'appRedux/createStore';
import { AUTH_STATE } from 'appRedux/types/reduxTypes';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { AuthService, ICasinoLinkedAccount, ILinkedAccounts } from 'services/auth';

import LinkPlayerCardModal from 'pages/MyPlayerCards/LinkPlayerCardModal';
import SmartLoading from 'components/SmartLoading';
import {
  bustLinkedAccountsCache,
  linkedAccountsBucket,
  playerScopedKey,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

interface ICasinoParams {
  casinoIDParam: string;
}

export const LinkPlayer: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const auth = new AuthService();
  const commands = ReduxCommandCreator(dispatch);
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;
  const { casinoIDParam } = useParams<ICasinoParams>();
  const casinoId = parseInt(casinoIDParam) || 1;

  const [presentToast] = useIonToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [hasCasinoAccess] = useState(true);

  const { userId, casinoSchema } = useAppSelector((state) => ({
    userId: state.app.persistedState.userInfo?.appUserPk,
    casinoSchema: state.app.persistedState.casinoSchema,
  }));

  const onCancel = () => {
    if (hasCasinoAccess || isProUser) {
      history.replace('/tabs/home');
    } else {
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: 'Player ID must be linked for access to each casino.',
      });
      history.replace('/auth/choosecasinos');
    }
    setShowForm(false);
  };

  const handleSuccessResponse = async () => {
    commands.SetAuthState(AUTH_STATE.AUTHENTICATED);
    try {
      if (userId) {
        bustLinkedAccountsCache(userId);
        const data = (await lastValueFrom(
          sharedPlayerScopedGet(linkedAccountsBucket, playerScopedKey(userId), () =>
            auth.GetLinkedAccounts(userId)
          )
        )) as ILinkedAccounts;
        if (data) {
          const linkedAccount = data.user.linkedAccounts as ICasinoLinkedAccount[];
          const casinoItem = linkedAccount?.find((item) => item.casinoId === casinoId);
          const isPlayerLinked = Boolean(casinoItem);
          const linkedCasinoId = casinoItem ? casinoItem.casinoId : null;
          casinoCommands.SetLinkedCasinos({
            casinoLinkedAccounts: linkedAccount,
            isPlayerLinked,
            linkedCasinoId,
          });
          setShowForm(false);
          history.replace('/tabs/home');
        }
      } else {
        throw new Error('no user id');
      }
    } catch {
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: 'Failed to retrieve linked accounts.',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const userInfo = await lastValueFrom(auth.GetUserInfo([casinoId]));
        if (userInfo && isMounted) {
          setIsLoading(false);
          setShowForm(true);
        }
      } catch {
        if (isMounted) {
          setIsLoading(false);
          void presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Failed to retrieve casino information.',
          });
        }
      }
    };

    if (isMounted) void fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <IonPage>
      <IonContent>
        <SmartLoading loading={isLoading} />
      </IonContent>
      {showForm && casinoSchema && (
        <LinkPlayerCardModal
          casinoInfo={casinoSchema}
          onSubmitSuccess={() => void handleSuccessResponse()}
          onCancel={onCancel}
        />
      )}
    </IonPage>
  );
};
